import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import {
  Alert,
  DataTable,
  Empty,
  Kpi,
  KpiGrid,
  Muted,
  PageHeader,
  Section,
  StatusBadge,
} from '@/components/ui'
import { fetchColaboradores } from '@/features/rh/api'
import type { AdmissaoIntegracao } from '@/features/rh/integracaoTypes'
import {
  createAdmissao,
  fetchAdmissoes,
  patchAdmissao,
  sincronizarAdmissoes,
} from './api'
import { STATUS_ADMISSAO } from './admissaoConstants'
import { AdmissaoWizard } from './AdmissaoWizard'
import { formatDate } from './utils'
import './admissao.css'

function statusTone(status?: string): 'ativo' | 'pendente' | 'inativo' {
  if (status === 'concluida' || status === 'integrado') return 'ativo'
  if (status === 'doc_pendente' || status === 'iniciada') return 'pendente'
  return 'inativo'
}

type Mode = 'lista' | 'nova'

export function AdmissaoPage() {
  const { canEdit } = useAuth()
  const editable = canEdit('dp-admissao')
  const qc = useQueryClient()
  const [params, setParams] = useSearchParams()
  const mode: Mode = params.get('nova') === '1' ? 'nova' : 'lista'

  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('')
  const [selected, setSelected] = useState<AdmissaoIntegracao | null>(null)
  const [msg, setMsg] = useState('')
  const [msgTone, setMsgTone] = useState<'info' | 'success' | 'danger'>('info')
  const [vincularId, setVincularId] = useState('')

  const lista = useQuery({
    queryKey: ['dp', 'admissoes', q, status],
    queryFn: () => fetchAdmissoes({ busca: q || undefined, status: status || undefined }),
  })

  const candidatos = useQuery({
    queryKey: ['dp', 'admissao', 'candidatos'],
    queryFn: () =>
      fetchColaboradores({
        onboarding: 'pendente',
        admitidos_dias: 180,
        por_pagina: 100,
      }),
    enabled: mode === 'lista' && editable,
  })

  const idsComAdmissao = useMemo(
    () => new Set((lista.data?.dados || []).map((a) => a.colaborador_id)),
    [lista.data],
  )

  const semAdmissao = useMemo(
    () => (candidatos.data?.dados || []).filter((c) => !idsComAdmissao.has(c.id)),
    [candidatos.data, idsComAdmissao],
  )

  const invalidate = async () => {
    await qc.invalidateQueries({ queryKey: ['dp', 'admissoes'] })
  }

  const syncMut = useMutation({
    mutationFn: () => sincronizarAdmissoes(90),
    onSuccess: async (res) => {
      const criados = res.criados || 0
      const restaurados = res.restaurados || 0
      const erros = res.erros || []
      if (criados + restaurados > 0) {
        setMsgTone(erros.length ? 'info' : 'success')
        const partes: string[] = []
        if (criados) partes.push(`${criados} criada(s)`)
        if (restaurados) partes.push(`${restaurados} restaurada(s)`)
        setMsg(
          `${partes.join(', ')} a partir de admitidos recentes.` +
            (erros.length ? ` ${erros.length} falha(s).` : ''),
        )
      } else {
        setMsgTone(erros.length ? 'danger' : 'success')
        setMsg(
          erros.length
            ? erros[0]?.erro || res.mensagem || 'Falha ao sincronizar.'
            : 'Nenhum colaborador pendente sem registro de admissão.',
        )
      }
      await invalidate()
      await qc.invalidateQueries({ queryKey: ['dp', 'admissao', 'candidatos'] })
    },
    onError: (err) => {
      setMsgTone('danger')
      setMsg(err instanceof ApiError ? err.message : 'Falha ao sincronizar.')
    },
  })

  const vincularMut = useMutation({
    mutationFn: (colaboradorId: string) => createAdmissao(colaboradorId),
    onSuccess: async (dados) => {
      setMsgTone('success')
      setMsg(`Onboarding iniciado para ${dados.colaborador_nome}.`)
      setSelected(dados)
      setVincularId('')
      await invalidate()
      await qc.invalidateQueries({ queryKey: ['dp', 'admissao', 'candidatos'] })
    },
    onError: (err) => {
      setMsgTone('danger')
      setMsg(err instanceof ApiError ? err.message : 'Falha ao vincular.')
    },
  })

  const patchMut = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string
      payload: Parameters<typeof patchAdmissao>[1]
    }) => patchAdmissao(id, payload),
    onSuccess: async (dados) => {
      setSelected(dados)
      setMsgTone('success')
      setMsg('Checklist atualizado.')
      await invalidate()
    },
    onError: (err) => {
      setMsgTone('danger')
      setMsg(err instanceof ApiError ? err.message : 'Falha ao atualizar.')
    },
  })

  const dados = lista.data?.dados || []
  const totais = lista.data?.totais

  function openNova() {
    setParams({ nova: '1' })
  }

  function closeNova() {
    setParams({})
  }

  if (mode === 'nova') {
    return (
      <section className="hi-page hi-admissao-page">
        <PageHeader
          backTo={{ to: '/app/dp/admissao', label: 'Admissão' }}
          title="Nova admissão"
          subtitle="Wizard completo — dados pessoais, contrato, dependentes, docs e benefícios."
        />
        {msg ? <Alert tone={msgTone}>{msg}</Alert> : null}
        <AdmissaoWizard
          onCancel={closeNova}
          onSuccess={async (admissaoId) => {
            setMsgTone('success')
            setMsg('Admissão registrada com sucesso.')
            closeNova()
            await invalidate()
            const found = (await fetchAdmissoes({})).dados?.find((a) => a.id === admissaoId)
            if (found) setSelected(found)
          }}
        />
      </section>
    )
  }

  return (
    <section className="hi-page hi-admissao-page">
      <PageHeader
        title="Admissão"
        subtitle="Fluxo de admissão e onboarding no Departamento Pessoal — API REST."
        actions={
          <>
            <Link className="btn btn-secondary" to="/app/central-integracao">
              Integração
            </Link>
            <a className="btn btn-secondary" href={legacyAppUrl('#/dp-admissao')}>
              Legado
            </a>
            {editable ? (
              <>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={syncMut.isPending}
                  onClick={() => syncMut.mutate()}
                >
                  {syncMut.isPending ? 'Sincronizando…' : 'Sincronizar (90d)'}
                </button>
                <button type="button" className="btn btn-primary" onClick={openNova}>
                  Nova admissão
                </button>
              </>
            ) : null}
          </>
        }
      />

      {msg ? <Alert tone={msgTone}>{msg}</Alert> : null}

      <KpiGrid>
        <Kpi label="Registros" value={String(totais?.total ?? dados.length)} />
        <Kpi label="Média checklist" value={`${totais?.media_pct ?? 0}%`} />
        <Kpi
          label="Pendentes"
          value={String(
            (totais?.por_status?.iniciada || 0) + (totais?.por_status?.doc_pendente || 0),
          )}
        />
        <Kpi label="Concluídas" value={String(totais?.por_status?.concluida || 0)} />
      </KpiGrid>

      {editable && semAdmissao.length > 0 ? (
        <Section title="Admitidos sem onboarding">
          <div className="hi-filters">
            <select
              value={vincularId}
              onChange={(e) => setVincularId(e.target.value)}
              aria-label="Colaborador sem admissão"
            >
              <option value="">Selecione um colaborador…</option>
              {semAdmissao.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                  {c.cargo_nome ? ` — ${c.cargo_nome}` : ''}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!vincularId || vincularMut.isPending}
              onClick={() => vincularMut.mutate(vincularId)}
            >
              Iniciar checklist
            </button>
          </div>
        </Section>
      ) : null}

      <form
        className="hi-filters"
        onSubmit={(e) => {
          e.preventDefault()
          setQ(search.trim())
        }}
      >
        <input
          type="search"
          placeholder="Buscar colaborador"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Busca"
        />
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          {STATUS_ADMISSAO.map((o) => (
            <option key={o.id || 'todos'} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
        <button type="submit" className="btn btn-secondary">
          Filtrar
        </button>
      </form>

      <div className="hi-admissao-layout">
        <Section title="Admissões em andamento" flush>
          {lista.isLoading ? <Muted>Carregando…</Muted> : null}
          {lista.isError ? (
            <Alert tone="danger">
              {lista.error instanceof ApiError ? lista.error.message : 'Falha ao listar.'}
            </Alert>
          ) : null}
          {!lista.isLoading && dados.length === 0 ? (
            <Empty>
              Nenhuma admissão. Use “Nova admissão” ou “Sincronizar” para criar a partir de
              colaboradores recentes.
            </Empty>
          ) : null}
          {dados.length > 0 ? (
            <DataTable>
              <thead>
                <tr>
                  <th>Colaborador</th>
                  <th>Admissão</th>
                  <th>Status</th>
                  <th>Checklist</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {dados.map((a) => (
                  <tr key={a.id} className={selected?.id === a.id ? 'hi-row-selected' : undefined}>
                    <td>
                      <strong>{a.colaborador_nome}</strong>
                      <Muted>
                        {[a.cargo_nome, a.departamento_nome].filter(Boolean).join(' · ') || '—'}
                      </Muted>
                    </td>
                    <td>
                      <Muted>{formatDate(a.data_admissao)}</Muted>
                    </td>
                    <td>
                      <StatusBadge tone={statusTone(a.status)}>
                        {a.status_display || a.status}
                      </StatusBadge>
                    </td>
                    <td>
                      <div className="hi-pct-bar" title={`${a.pct_checklist ?? 0}%`}>
                        <span style={{ width: `${a.pct_checklist ?? 0}%` }} />
                      </div>
                      <Muted>{a.pct_checklist ?? 0}%</Muted>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setSelected(a)}
                      >
                        Checklist
                      </button>
                      <Link className="btn btn-secondary btn-sm" to={`/app/rh/${a.colaborador_id}`}>
                        Ficha
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          ) : null}
        </Section>

        {selected ? (
          <Section
            title={`Checklist — ${selected.colaborador_nome}`}
            actions={
              editable ? (
                <select
                  value={selected.status}
                  aria-label="Alterar status"
                  disabled={patchMut.isPending}
                  onChange={(e) =>
                    patchMut.mutate({
                      id: selected.id,
                      payload: { status: e.target.value },
                    })
                  }
                >
                  {STATUS_ADMISSAO.filter((o) => o.id).map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : null
            }
          >
            <ul className="hi-checklist">
              {Object.entries(selected.checklist || {}).map(([id, item]) => (
                <li key={id}>
                  <label className="hi-check">
                    <input
                      type="checkbox"
                      checked={Boolean(item.ok)}
                      disabled={!editable || patchMut.isPending}
                      onChange={(e) =>
                        patchMut.mutate({
                          id: selected.id,
                          payload: {
                            checklist_item: { id, ok: e.target.checked },
                          },
                        })
                      }
                    />
                    <span>{item.label || id}</span>
                  </label>
                  {item.owner ? <Muted>{item.owner}</Muted> : null}
                </li>
              ))}
            </ul>

            <div className="hi-integracao-flags">
              {(
                [
                  ['contratos_gerados', 'Contratos gerados'],
                  ['acesso_sistemas', 'Acesso aos sistemas'],
                  ['exame_admissional', 'Exame admissional'],
                  ['epi_entregue', 'EPI entregue'],
                  ['esocial_enviado', 'eSocial enviado'],
                ] as const
              ).map(([key, label]) => (
                <label key={key} className="hi-check">
                  <input
                    type="checkbox"
                    checked={Boolean(selected[key])}
                    disabled={!editable || patchMut.isPending}
                    onChange={(e) =>
                      patchMut.mutate({
                        id: selected.id,
                        payload: { [key]: e.target.checked },
                      })
                    }
                  />
                  {label}
                </label>
              ))}
            </div>

            {selected.dados_wizard && Object.keys(selected.dados_wizard).length > 0 ? (
              <details className="hi-admissao-wizard-data">
                <summary>Dados do wizard (formação, saúde, docs…)</summary>
                <pre className="hi-prose">
                  {JSON.stringify(selected.dados_wizard, null, 2)}
                </pre>
              </details>
            ) : null}
          </Section>
        ) : null}
      </div>
    </section>
  )
}

export default AdmissaoPage
